import { useTranslation } from "@sagot/i18n";
import { Badge, DropdownMenuItem, DropdownMenuSeparator, Icon } from "@sagot/ui";
import { canRestore } from "./defaults-model";

const D = "engage.workflows.defaults";

/** What the builder does for a default beyond what it does for any workflow. */
export interface DefaultActions {
  /** The plan has workflows of the workspace's own: Duplicate works; otherwise it opens the upgrade sheet. */
  canDuplicate: boolean;
  onRestore: () => void;
  onCompare: () => void;
  onDuplicate: () => void;
}

interface DefaultHeaderMenuProps extends DefaultActions {
  canManage: boolean;
  canPublish: boolean;
  workflow: { is_modified: boolean; has_default_update: boolean; has_unpublished_changes: boolean };
}

/**
 * The More-menu items of a default: Duplicate, Restore default, Compare with default, and a Delete that is there but off, with
 * the reason, so nobody looks for it. (Translations are not offered: a default has no customer-facing text.)
 */
export function DefaultHeaderMenu({ canManage, canPublish, workflow, canDuplicate, onRestore, onCompare, onDuplicate }: DefaultHeaderMenuProps) {
  const { t } = useTranslation();
  const restorable = canRestore(workflow);
  return (
    <>
      {canManage ? (
        <DropdownMenuItem onSelect={onDuplicate}>
          <Icon name="copy" />
          <span className="flex-1">{t(`${D}.menu.duplicate`)}</span>
          {canDuplicate ? null : <Badge><Icon name="lock" className="size-3" />{t("engage.workflows.planNeeded", { plan: t("console.plans.standard") })}</Badge>}
        </DropdownMenuItem>
      ) : null}
      {canPublish ? (
        <DropdownMenuItem disabled={!restorable} onSelect={onRestore}>
          <Icon name="rotate-ccw" />
          {t(restorable ? `${D}.menu.restore` : `${D}.menu.restoreDisabled`)}
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem onSelect={onCompare}>
        <Icon name="layers" />
        {t(`${D}.menu.compare`)}
      </DropdownMenuItem>
      {canManage ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled>
            <Icon name="trash" />
            <span>
              {t("engage.workflows.builder.delete")}
              <span className="block text-xs text-fg-subtle">{t(`${D}.builder.deleteReason`)}</span>
            </span>
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  );
}
